<template>
    <section class="container py-4">
        <div class="mb-4">
            <div class="card p-3">
                <div class="row">
                    <div class="col-md-4 select_filter" v-for="filterItem in filters" :key="filterItem.id">
                        <label :for="'filter-' + filterItem.id" class="form-label">
                            {{ filterItem.name }}
                        </label>
                        <select
                            :id="'filter-' + filterItem.id"
                            v-model="options.filter.selectedFilters[filterItem.id]"
                            @change="search"
                            class="form-select"
                        >
                            <option :value="null">---</option>
                            <option
                                v-for="option in filterItem.options"
                                :key="option.id"
                                :value="option.id"
                            >
                                {{ option.name }}
                            </option>
                        </select>
                        <div v-if="messageFieldError('filter.options')" class="text-danger mt-1">
                            {{ messageFieldError('filter.options') }}
                        </div>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-md-4 select_filter">
                        <label class="form-label">Search By</label>
                        <select class="form-select" v-model="options.filter.search_field">
                            <option v-for="searchField in searchFields" :key="searchField.value" :value="searchField.value">
                                {{ searchField.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-4">
                        <label class="form-label">Search Value</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="options.filter.search_value"
                        />
                    </div>
                    <div class="col-md-4 d-flex align-items-end gap-2">
                        <button class="btn btn-primary py-1 px-5" :disabled="isLoading" @click="search"><i class="fas fa-search" title="Search"></i></button>
                        <button class="btn btn-secondary py-1 ml-3 px-5" :disabled="isLoading" @click="clear"><i class="fas fa-times" title="Clear"></i></button>
                    </div>
                </div>
            </div>
        </div>

        <div class="table-responsive">
            <table class="table table-bordered table-striped">
                <thead>
                <tr>
                    <th
                        v-for="(header, i) in headers"
                        :key="i"
                    >
                        {{ header.text }}
                        <span v-if="header.value === 'title'" class="ms-2" @click="sort">
                                <img
                                    :src="sort === 'asc' ? '/images/sort_up.gif' : '/images/sort_down.gif'"
                                    alt="Sort"
                                    style="cursor: pointer;"
                                />
                            </span>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in items" :key="item.id">
                    <td v-for="(header, i) in headers" :key="i" class="align-top">
                        <template v-if="header.value === 'buttons'">
                            <span><a :href="`/library-catalog/${item.id}`"><i class="fas fa-eye" title="Show"></i></a></span>
                        </template>

                        <span v-html="item[header.value]"></span>
                    </td>
                </tr>
                <tr v-if="items.length === 0">
                    <td :colspan="headers.length" class="text-center fw-bold py-3">
                        No Records Found
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="d-flex justify-content-center mt-4">
            <pagination
                v-model="options.page"
                :records="count"
                :per-page="options.itemsPerPage"
                @paginate="loadData"
                :options="{
                    chunksNavigation: 'scroll',
                    edgeNavigation: true
                }"
            />
        </div>
    </section>
</template>

<script>
import Pagination from 'vue-pagination-2';

export default {
    name: "LibraryComponent",
    components: {
        Pagination
    },
    data() {
        return {
            isLoading: false,
            items: [],
            count: 0,
            headers: [],
            library_settings: {
                title: '',
                entry_page: null,
                show_per_page: null,
                classification_system: null,
                resource_info: {},
                cataloging: {},
                acquisition_info: {},
                administrative_only: {},
                udf: {
                    custom_field1: '',
                    custom_field2: '',
                    custom_field3: '',
                    custom_field4: '',
                    custom_field5: '',
                    custom_field6: '',
                },
                custom_text_field: '',
            },
            searchFields: [],
            filters: [],
            options: {
                page: 1,
                itemsPerPage: 15,
                sort: 'asc',
                filter: {
                    search_field: '',
                    search_value: '',
                    selectedFilters: {},
                    search_fields: [],
                },
            }
        }
    },
    computed: {},
    mounted() {
        this.loadOptionsFromUrl();
        this.loadData();
        this.loadHeaders();
        this.loadSettings();
        this.loadSearchFields();
        this.loadFilters();
    },
    methods: {
        loadData() {
            this.isLoading = true;

            this.$api.get('api/v1/library/items', {params: this.options}).then(response => {
                if (response.data) {
                    this.count = response.data.meta.total;
                    this.page = response.data.meta?.current_page;
                    this.items = response.data.data;

                    this.updateUrl();
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        updateUrl() {
            const queryParams = new URLSearchParams();

            queryParams.set('page', this.options.page);
            queryParams.set('itemsPerPage', this.options.itemsPerPage);
            queryParams.set('sort', this.options.sort);

            if (this.options.filter) {
                queryParams.set('filter', JSON.stringify(this.options.filter));
            }

            window.history.pushState({}, '', `${window.location.pathname}?${queryParams.toString()}`);
        },
        loadOptionsFromUrl() {
            const queryParams = new URLSearchParams(window.location.search);

            this.options.page = parseInt(queryParams.get('page')) || this.options.page;
            this.options.itemsPerPage = parseInt(queryParams.get('itemsPerPage')) || this.options.itemsPerPage;
            this.options.sort = queryParams.get('sort') || this.options.sort;

            const filter = queryParams.get('filter');

            if (filter) {
                try {
                    const parseFilter = JSON.parse(filter)

                    if (parseFilter) {
                        this.options.filter = parseFilter;
                    }
                } catch (error) {
                    console.error('Failed to parse filter from URL:', error);
                }
            }
        },
        loadSettings() {
            this.isLoading = true;

            this.$api.get('api/v1/admin/settings/library').then(response => {
                if (response.data) {
                    this.library_settings = response.data[0]?.value;
                    this.options.itemsPerPage = response.data[0]?.value?.items_per_page || 15;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadHeaders() {
            this.isLoading = true;

            this.$api.get('api/v1/library/items/headers', {params: this.options}).then(response => {
                if (response.data) {
                    this.headers = response.data;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadSearchFields() {
            this.isLoading = true;

            this.$api.get('api/v1/library/items/search-fields').then(response => {
                if (response.data) {
                    this.searchFields = response.data;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadFilters() {
            this.isLoading = true;

            this.$api.get('api/v1/library/filters/list').then(response => {
                if (response.data) {
                    this.filters = response.data.data;

                    this.filters.forEach(filter => {
                        if (!(filter.id in this.options.filter.selectedFilters)) {
                            this.options.filter.selectedFilters[filter.id] = null;
                        }
                    });
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        sort() {
            this.options.sort = this.options.sort === 'asc' ? 'desc' : 'asc';
            this.loadData();
        },
        search() {
            this.options.page = 1;
            this.loadHeaders();
            this.loadData();
        },
        clear() {
            this.options.filter = {
                search_field: '',
                search_value: '',
                selectedFilters: {},
                search_fields: [],
            };

            this.loadData();
        }
    }
}
</script>

<style scoped lang="scss">
.VuePagination__pagination li {
    list-style: none !important;
}

.paginate-wrapper {
    .page-link {
        cursor: pointer;
    }
}

.table th,
.table td {
    text-align: left;
    vertical-align: middle;
}

.fa-eye {
    color: #0b100b;
    cursor: pointer;
}

.fa-eye:hover {
    color: #0F192A;
}

.select_filter select {
    display: block;
    width: 100%;
    height: 38px;
}
</style>
